/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './CustomScrollbarStyles.scss';
import { SCROLL_BAR_CONTAINER_ID, SCROLL_KEY_STEP } from './constants';
import { getPaginationPinOffset, getScrollbarContainer, getScrollbarLine, getWidestScrollableChild, isElementBottomVisible, isElementVisible, syncScrollbarAriaValue } from './utils';
import useAddEventListeners from './hooks/useAddEventListeners';
import useAddResizeObserver from './hooks/useAddResizeObserver';
function CustomScrollbarInternal({ children }) {
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const currentPosition = useRef(0);
    const scrollSpace = useRef(0);
    const scrollRatio = useRef(1);
    const lastScrollableElement = useRef(null);
    const scrollbarPrefixId = useMemo(() => uuidv4(), []);
    const handleDrag = useCallback((event) => {
        if (!isDragging.current)
            return;
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        const container = getScrollbarContainer(scrollbarPrefixId);
        if (!scrollLine || !container)
            return;
        const isRelative = scrollLine.style.position === 'relative';
        const scrollableElement = getWidestScrollableChild(container);
        if (!scrollableElement)
            return;
        const scrollLeftAmount = event.clientX - dragStartX.current;
        const initialLeftPosition = isRelative ? 0 : container.getBoundingClientRect().x;
        const newScrollableElementLeft = currentPosition.current + scrollLeftAmount - initialLeftPosition;
        const newLeftPosition = currentPosition.current + scrollLeftAmount;
        if (newLeftPosition < initialLeftPosition || newLeftPosition > scrollSpace.current + initialLeftPosition) {
            if (newLeftPosition < initialLeftPosition) {
                scrollLine.style.left = `${initialLeftPosition}px`;
                scrollableElement.scrollLeft = 0;
            }
            else {
                scrollLine.style.left = `${scrollSpace.current + initialLeftPosition}px`;
                scrollableElement.scrollLeft = scrollSpace.current * scrollRatio.current;
            }
            syncScrollbarAriaValue(scrollLine, scrollableElement);
            return;
        }
        scrollLine.style.left = `${newLeftPosition}px`;
        scrollableElement.scrollLeft = newScrollableElementLeft * scrollRatio.current;
        syncScrollbarAriaValue(scrollLine, scrollableElement);
    }, [scrollbarPrefixId]);
    const handleDragEnd = () => {
        document.body.style.userSelect = 'auto';
        isDragging.current = false;
    };
    const handleUserWheelScroll = useCallback((event) => {
        if (isDragging.current)
            return;
        const target = event.target;
        currentPosition.current = target.scrollLeft;
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        const container = getScrollbarContainer(scrollbarPrefixId);
        if (!scrollLine || !container)
            return;
        const containerLeft = container.getBoundingClientRect().x;
        const initialLeftPosition = scrollLine.style.position === 'relative' ? 0 : containerLeft;
        scrollLine.style.left = `${target.scrollLeft / scrollRatio.current + initialLeftPosition}px`;
        syncScrollbarAriaValue(scrollLine, target);
    }, [scrollbarPrefixId]);
    const handleScrollbarKeyDown = useCallback((event) => {
        const container = getScrollbarContainer(scrollbarPrefixId);
        const scrollableElement = getWidestScrollableChild(container);
        if (!scrollableElement)
            return;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            scrollableElement.scrollLeft -= SCROLL_KEY_STEP;
        }
        else if (event.key === 'ArrowRight') {
            event.preventDefault();
            scrollableElement.scrollLeft += SCROLL_KEY_STEP;
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            scrollableElement.scrollLeft = 0;
        }
        else if (event.key === 'End') {
            event.preventDefault();
            scrollableElement.scrollLeft = scrollableElement.scrollWidth;
        }
        else {
            return;
        }
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        syncScrollbarAriaValue(scrollLine, scrollableElement);
    }, [scrollbarPrefixId]);
    const handleVerticalScroll = useCallback(() => {
        const container = getScrollbarContainer(scrollbarPrefixId);
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        if (!container || !scrollLine)
            return;
        const isVisible = isElementVisible(container);
        const isBottomVisible = isElementBottomVisible(lastScrollableElement.current);
        const scrollableElement = getWidestScrollableChild(container);
        if (!scrollableElement)
            return;
        const isFixed = scrollLine.style.position !== 'relative';
        const isRelative = scrollLine.style.position !== 'fixed';
        if (isVisible && !isBottomVisible) {
            const pinOffset = getPaginationPinOffset(container);
            if (isRelative) {
                scrollLine.style.position = 'fixed';
                const containerLeft = container.getBoundingClientRect().x;
                scrollLine.style.left = `${containerLeft + scrollableElement.scrollLeft / scrollRatio.current}px`;
            }
            scrollLine.style.bottom = `${pinOffset}px`;
        }
        else if (isFixed) {
            scrollLine.style.position = 'relative';
            scrollLine.style.left = `${scrollableElement.scrollLeft / scrollRatio.current}px`;
            scrollLine.style.bottom = '';
        }
    }, [scrollbarPrefixId]);
    const handleDragStart = useCallback((event) => {
        const scrollLine = getScrollbarLine(scrollbarPrefixId);
        if (!scrollLine)
            return;
        const currentLineLeft = parseInt(window.getComputedStyle(scrollLine).getPropertyValue('left'), 10) || 0;
        isDragging.current = true;
        document.body.style.userSelect = 'none';
        dragStartX.current = event.clientX;
        currentPosition.current = currentLineLeft;
    }, [scrollbarPrefixId]);
    useAddResizeObserver({
        scrollbarPrefixId,
        scrollSpaceRef: scrollSpace,
        scrollRatioRef: scrollRatio,
        handleVerticalScroll,
        handleDragStart,
        handleUserWheelScroll,
        handleScrollbarKeyDown,
        lastScrollableElementRef: lastScrollableElement
    });
    useAddEventListeners({
        handleDrag,
        handleDragEnd,
        handleVerticalScroll
    });
    return (React.createElement("div", { role: "presentation", id: `${SCROLL_BAR_CONTAINER_ID}${scrollbarPrefixId}`, className: "qmb-ui-custom-scrollbar custom-scrollbar-container" }, children));
}
/**
 * CustomScrollbar
 * Sticky/fixed horizontal scrollbar for overflow containers (listing tables).
 * Hidden on touch devices so native scrolling is used.
 *
 * @version 1.0.0
 * @since 2026-08-18
 */
export function CustomScrollbar({ children = null }) {
    const [shouldShowScrollBar] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
        return !isTouchDevice;
    });
    if (!shouldShowScrollBar) {
        return React.createElement(React.Fragment, null, children);
    }
    return React.createElement(CustomScrollbarInternal, null, children);
}
CustomScrollbar.displayName = 'CustomScrollbar';
